import { createApi } from '@reduxjs/toolkit/query/react';
import {
  AnnotationSet,
  DashboardData,
  Review,
  ReviewFrame,
  ReviewFramesBySegmentationUpdatedAtParams,
  ReviewFrameUpdate,
  ReviewIn,
} from '../../models/reviews';
import { baseQueryWithAuthRedirect } from './rtk';

export const reviewsAPI = createApi({
  reducerPath: 'reviewsAPI',
  baseQuery: baseQueryWithAuthRedirect,
  tagTypes: ['Reviews'],
  endpoints: builder => ({
    getReviews: builder.query<ReviewIn[], string>({
      query: () => ({
        url: 'api/v1/reviews/?paginate=false',
      }),
      providesTags: [{ type: 'Reviews', id: 'current' }],
    }),
    getDashboardData: builder.query<DashboardData, void>({
      query: () => ({
        url: 'api/v1/dashboard/',
      }),
      providesTags: [{ type: 'Reviews', id: 'dashboard' }],
    }),
    getAnnotationSets: builder.query<AnnotationSet[], void>({
      query: () => ({
        url: 'api/v1/full-study-annotation-sets/',
        params: { paginate: false },
      }),
    }),
    getReviewFrames: builder.query<ReviewFrame[], string>({
      query: annotationSet => ({
        url: 'api/v1/review-frames/',
        params: {
          annotation_set: annotationSet,
          paginate: false,
          segmented_frames: true,
        },
      }),
      providesTags: [{ type: 'Reviews', id: 'frames' }],
    }),
    getReviewFramesBySegmentationUpdatedAt: builder.query<
      ReviewFrame[],
      ReviewFramesBySegmentationUpdatedAtParams
    >({
      query: ({ updatedAtAfter, updatedAtBefore }) => ({
        url: 'api/v1/review-frames/',
        params: {
          paginate: false,
          segmented_frames: true,
          ...(updatedAtAfter ? { segmentation_updated_at_after: updatedAtAfter } : {}),
          ...(updatedAtBefore ? { segmentation_updated_at_before: updatedAtBefore } : {}),
        },
      }),
      providesTags: [{ type: 'Reviews', id: 'frames' }],
    }),
    getThreeDReviewFrames: builder.query<ReviewFrame[], string>({
      query: studyIcid => ({
        url: 'api/v1/review-frames/',
        params: {
          decision: '3D',
          paginate: false,
          study_icid: studyIcid,
        },
      }),
      providesTags: [{ type: 'Reviews', id: 'three-d-frames' }],
    }),
    getDismissedThreeDReviewFrames: builder.query<ReviewFrame[], string>({
      query: studyIcid => ({
        url: 'api/v1/review-frames/',
        params: {
          decision: '3D',
          paginate: false,
          status: 'DISMISSED',
          study_icid: studyIcid,
        },
      }),
      providesTags: [{ type: 'Reviews', id: 'three-d-frames' }],
    }),
    updateReviewFrame: builder.mutation<void, ReviewFrameUpdate>({
      query: ({
        annotationSet,
        comment,
        dicomId,
        dicomIcid,
        frameIndex,
        decision,
        status,
        segmentationUpdatedAtAfter,
        segmentationUpdatedAtBefore,
      }) => {
        const body = {
          ...(annotationSet ? { annotation_set: annotationSet } : {}),
          ...(dicomId !== undefined ? { dicom_id: dicomId } : {}),
          ...(dicomIcid ? { dicom_icid: dicomIcid } : {}),
          frame_index: frameIndex,
          ...(segmentationUpdatedAtAfter
            ? { segmentation_updated_at_after: segmentationUpdatedAtAfter }
            : {}),
          ...(segmentationUpdatedAtBefore
            ? { segmentation_updated_at_before: segmentationUpdatedAtBefore }
            : {}),
          ...(comment !== undefined ? { comment } : {}),
          ...(decision ? { decision } : {}),
          ...(status ? { status } : {}),
        };
        return {
          url: 'api/v1/review-frames/',
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [
        { type: 'Reviews', id: 'frames' },
        { type: 'Reviews', id: 'three-d-frames' },
      ],
    }),
    createReview: builder.mutation<ReviewIn[], Review | Review[]>({
      query: (review: Review | Review[]) => ({
        url: 'api/v1/reviews/',
        method: 'POST',
        body: Array.isArray(review) ? review : [review],
      }),
      invalidatesTags: (result, error, review) => [
        { type: 'Reviews', id: 'current' },
        { type: 'Reviews', id: 'dashboard' },
        {
          type: 'Views' as 'Reviews',
          id: Array.isArray(review) ? review[0].object_view_id : review.object_view_id,
        },
      ],
    }),
    getSegmentationStats: builder.query<
      {
        totalFound: number;
        accepted: number;
        rejected: number;
        toBeReviewed: number;
      },
      void
    >({
      query: () => 'api/v1/segmentation-labels/stats/',
    }),
    getKeypointStats: builder.query<
      {
        totalFound: number;
        accepted: number;
        rejected: number;
        toBeReviewed: number;
      },
      void
    >({
      query: () => 'api/v1/keypoint-collection-labels/stats/',
    }),
    getSegmentationPredictionStats: builder.query<
      {
        totalFound: number;
        accepted: number;
        rejected: number;
        toBeReviewed: number;
      },
      void
    >({
      query: () => 'api/v1/segmentation-predictions/stats/',
    }),
    deleteReview: builder.mutation<void, string>({
      query: id => ({
        url: `api/v1/reviews/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Reviews', id: 'current' },
        { type: 'Reviews', id: 'dashboard' },
      ],
    }),
    bulkDeleteReviews: builder.mutation<
      { deleted: number },
      { object_uuids?: string[]; review_uuids?: string[]; object_type?: string }
    >({
      query: payload => ({
        url: 'api/v1/reviews/',
        method: 'DELETE',
        body: payload,
      }),
      invalidatesTags: [
        { type: 'Reviews', id: 'current' },
        { type: 'Reviews', id: 'dashboard' },
      ],
    }),
  }),
});
export const {
  useGetReviewsQuery,
  useLazyGetReviewsQuery,
  useGetDashboardDataQuery,
  useGetAnnotationSetsQuery,
  useLazyGetReviewFramesQuery,
  useLazyGetReviewFramesBySegmentationUpdatedAtQuery,
  useGetThreeDReviewFramesQuery,
  useGetDismissedThreeDReviewFramesQuery,
  useUpdateReviewFrameMutation,
  useCreateReviewMutation,
  useDeleteReviewMutation,
  useBulkDeleteReviewsMutation,
  useGetSegmentationStatsQuery,
  useGetKeypointStatsQuery,
  useGetSegmentationPredictionStatsQuery,
} = reviewsAPI;
